import { BasePopup } from 'app/components/BasePopup';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ISharedEquipmentEntry } from 'types/IEquipmentEntry';
import './EquipmentAccessSharingDialog.less';
import { AccessSharingRole } from 'types/entities/AccessSharingRole';
import {
  EquipmentAccessSharingDialogItem,
  EquipmentAccessSharingDialogPartOfText,
  EquipmentAccessSharingDialogMyRoleText,
} from './components';
import { UserAvatarStack } from 'app/components/access-sharing';
import { AddIcon, TrashIcon } from 'app/components/icons';
import useHasAccessSharingPermissions from 'app/hooks/useHasAccessSharingPermissions';
import { useNavigate } from 'react-router-dom';
import { DeleteDialog } from 'app/components/dialogs';
import { useNotificationsContext } from 'context';
import { ErrorNotification, Success } from 'app/components/Notifications';
import { SharedItemShareType } from 'services/api/shares';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import { invitationsApi } from 'services/api/invitations/queries';

interface Props {
  anchor?: HTMLElement;
  equipment: ISharedEquipmentEntry;
  onClose: () => void;
}

export default function EquipmentAccessSharingDialog({
  anchor,
  equipment,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const { optimisticallyRemoveEquipments } = useContext(EquipmentListContext);

  const isShared =
    equipment?.isSharedIndividually || equipment?.isSharedAsPartOfFleet;

  // TODO will be removed when API is ready.
  const role = equipment?.role || AccessSharingRole.Viewer;

  const { t } = useTranslation();
  const hasAccess = useHasAccessSharingPermissions(role);

  const isOwner = role === AccessSharingRole.Owner;
  const isAdmin = role === AccessSharingRole.Admin;

  const isShareAsEquipmentOptionVisible =
    isOwner &&
    !equipment?.isSharedIndividually &&
    !equipment?.isSharedAsPartOfFleet;
  const isShareAsPartOfFleetOptionVisible =
    isOwner &&
    !equipment?.isSharedAsPartOfFleet &&
    !equipment?.isSharedIndividually;
  const isManageFleetOptionVisible =
    hasAccess('editFleet') && equipment?.isSharedAsPartOfFleet;
  const isManageUsersOptionVisible =
    hasAccess('addNewUser') &&
    (equipment?.isSharedIndividually || equipment?.isSharedAsPartOfFleet);
  const isViewFleetOptionVisible = !isOwner && equipment?.isSharedAsPartOfFleet;
  const isSharedWithYouOptionVisible =
    !isOwner && !isAdmin && equipment?.isSharedIndividually;
  const isAddNewUserOptionVisible = hasAccess('addNewUser') && isShared;
  const isRevokeMyAccessOptionVisible =
    hasAccess('revokeOwnAccess') && isShared;
  const [revokeInvitation] = invitationsApi.useRevokeInvitationMutation();
  const { addNotification } = useNotificationsContext();

  const { data: user } = useGetUserInfoQuery();

  const [isRevokeOwnAccessDialogOpen, setIsRevokeOwnAccessDialogOpen] =
    useState(false);

  const handleRevokeAccess = async () => {
    try {
      const exposedAccessSharingRecordId =
        equipment?.shareData?.sharedWith.find(
          sharedWith => sharedWith.email === user?.email,
        )?.exposedAccessSharingRecordId;

      if (!exposedAccessSharingRecordId || !equipment?.isSharedIndividually)
        return;

      await revokeInvitation([
        {
          shareType: SharedItemShareType.EQUIPMENT,
          exposedAccessSharingRecordId,
        },
      ]);
      optimisticallyRemoveEquipments([equipment.id]);
      addNotification(
        <Success text={t('access_sharing.snackbars.access-revoked')} />,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={t('server_error_message')} />);
    } finally {
      setIsRevokeOwnAccessDialogOpen(false);
    }
  };

  return (
    <>
      <BasePopup
        className="equipment-access-sharing-dialog"
        anchor={anchor}
        onClose={onClose}
        ignoreAnchorOnMobile
      >
        <h2 className="equipment-access-sharing-dialog__title">
          {t('equipment_page.dialogs.access_sharing_dialog.title')}
        </h2>

        <ul className="equipment-access-sharing-dialog__list">
          {isShareAsEquipmentOptionVisible && (
            <EquipmentAccessSharingDialogItem
              title={t(
                'equipment_page.dialogs.access_sharing_dialog.options.share_as_equipment.title',
              )}
              description={t(
                'equipment_page.dialogs.access_sharing_dialog.options.share_as_equipment.description',
              )}
              onClick={() =>
                navigate(`/access/equipment/${equipment?.id}/share`)
              }
            />
          )}

          {isShareAsPartOfFleetOptionVisible && (
            <EquipmentAccessSharingDialogItem
              title={t(
                'equipment_page.dialogs.access_sharing_dialog.options.share_as_part_of_fleet.title',
              )}
              description={t(
                'equipment_page.dialogs.access_sharing_dialog.options.share_as_part_of_fleet.description',
              )}
              onClick={() =>
                navigate(`/access/equipment/${equipment?.id}/share-fleet`)
              }
            />
          )}

          {isManageFleetOptionVisible && (
            <EquipmentAccessSharingDialogItem
              title={t(
                'equipment_page.dialogs.access_sharing_dialog.options.manage_fleet.title',
              )}
              description={t(
                'equipment_page.dialogs.access_sharing_dialog.options.manage_fleet.description',
              )}
              onClick={() => navigate(`/equipment/${equipment?.id}/fleet`)}
            >
              <EquipmentAccessSharingDialogPartOfText
                fleetName={
                  equipment?.fleetData?.sharedItemDetails.sharedItemLabel || ''
                }
              />
            </EquipmentAccessSharingDialogItem>
          )}

          {isManageUsersOptionVisible && (
            <EquipmentAccessSharingDialogItem
              title={t(
                'equipment_page.dialogs.access_sharing_dialog.options.manage_users.title',
              )}
              description={
                isOwner
                  ? t(
                      'equipment_page.dialogs.access_sharing_dialog.options.manage_users.owner_description',
                    )
                  : t(
                      'equipment_page.dialogs.access_sharing_dialog.options.manage_users.description',
                      {
                        name: equipment?.shareData?.sharedItemDetails.ownerName,
                      },
                    )
              }
              onClick={() =>
                navigate(`/equipment/${equipment?.id}/manage-users`)
              }
            >
              <UserAvatarStack
                users={
                  equipment.shareData?.sharedWith.map(sharedWith => ({
                    firstName: sharedWith.displayName,
                  })) || []
                }
              />

              <EquipmentAccessSharingDialogMyRoleText role={role} />
            </EquipmentAccessSharingDialogItem>
          )}

          {isViewFleetOptionVisible && (
            <EquipmentAccessSharingDialogItem
              title={t(
                'equipment_page.dialogs.access_sharing_dialog.options.view_fleet.title',
              )}
              description={t(
                'equipment_page.dialogs.access_sharing_dialog.options.view_fleet.description',
              )}
              onClick={() => navigate(`/equipment/${equipment?.id}/fleet`)}
            >
              <EquipmentAccessSharingDialogPartOfText
                fleetName={
                  equipment?.fleetData?.sharedItemDetails.sharedItemLabel || ''
                }
              />
            </EquipmentAccessSharingDialogItem>
          )}

          {isSharedWithYouOptionVisible && (
            <EquipmentAccessSharingDialogItem
              title={t(
                'equipment_page.dialogs.access_sharing_dialog.options.shared_with_you.title',
              )}
              description={t(
                'equipment_page.dialogs.access_sharing_dialog.options.shared_with_you.description',
                { name: equipment?.shareData?.sharedItemDetails.ownerName },
              )}
              onClick={() =>
                navigate(`/equipment/${equipment?.id}/manage-users`)
              }
            >
              <EquipmentAccessSharingDialogMyRoleText role={role} />
            </EquipmentAccessSharingDialogItem>
          )}

          {isAddNewUserOptionVisible && (
            <EquipmentAccessSharingDialogItem
              title={t(
                'equipment_page.dialogs.access_sharing_dialog.options.add_new_user.title',
              )}
              icon={AddIcon}
              onClick={() =>
                navigate(`/equipment/${equipment.id}/manage-users/add-new-user`)
              }
            />
          )}

          {isRevokeMyAccessOptionVisible && (
            <EquipmentAccessSharingDialogItem
              title={t(
                'equipment_page.dialogs.access_sharing_dialog.options.revoke_my_access.title',
              )}
              icon={TrashIcon}
              onClick={() => {
                onClose();
                setIsRevokeOwnAccessDialogOpen(true);
              }}
            />
          )}
        </ul>
      </BasePopup>

      {isRevokeOwnAccessDialogOpen && (
        <DeleteDialog
          open
          title={t('revoke_access_confirmation', {
            email: user?.email,
            machineName: equipment?.nickName || equipment?.model,
          })}
          confirmationLabel={t('access_sharing.role_based_actions.revoke')}
          onClose={() => setIsRevokeOwnAccessDialogOpen(false)}
          onConfirm={handleRevokeAccess}
        />
      )}
    </>
  );
}
